import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { createFilterContext } from "./filterContext";
import ErrorBoundary from "./components/common/ErrorBoundary";
import YearComparison from "./components/YearComparison";
import MonthlyPerformance from "./components/MonthlyPerformance";
import ClosedLostPredictor from "./components/ClosedLostPredictor";
import ClosedLostReasons from "./components/ClosedLostReasons";
import GoodPairs from "./components/GoodPairs";
import BadPairs from "./components/BadPairs";
import TopCompanies from "./components/TopCompanies";
import ProbabilityAnalysis from "./components/ProbabilityAnalysis";
import PipelineOverview from "./components/PipelineOverview";
import FOCSampleTracker from "./components/FOCSampleTracker";
import VariationTracker from "./components/VariationTracker";
import ForecastedBooking from "./components/ForecastedBooking";
import AIAssistant from "./components/AIAssistant";
import AnomalyAssistant from "./components/AnomalyAssistant";
import CursorFollower from "./components/common/CursorFollower";
import Login from "./components/Login";
import UserManagement from "./components/UserManagement";
import { canViewAllSalespersons } from "./utils/roleUtils";
import { getDisplayCurrency } from "./utils/currency";
import { API_BASE } from "./api/config";

const EXCLUDED_SALESPEOPLE = new Set([
  "psg sales",
  "sachin abdul basheer",
  "john joseph",
  "ameen abouelez",
  "mageeesh mani",
  "mageesh mani",
  "nimesh nair",
  "nihal",
  "muhamed nihal",
  "rigvender singh",
]);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');

  const [salespeople, setSalespeople] = useState([]);
  const [selectedSalesperson, setSelectedSalesperson] = useState("");
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedRegion, setSelectedRegion] = useState("all");
  const availableRegions = [
    "all",
    "Blue Rhine Industries",
    "Professional Signs",
    "ProSigns KSA",
    "ProSigns Oman",
    "ProSigns Qatar",
  ];
  const [availableYears, setAvailableYears] = useState([currentYear]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fullScreen, setFullScreen] = useState(null);
  const [dashboardBundle, setDashboardBundle] = useState(null);
  const [closedLostData, setClosedLostData] = useState([]);
  const [goodBadMargin, setGoodBadMargin] = useState(null);
  const [goodBadMarginLoading, setGoodBadMarginLoading] = useState(false);
  const [goodBadMarginError, setGoodBadMarginError] = useState("");
  const [concerningEnquiries, setConcerningEnquiries] = useState([]);
  const [concerningLoading, setConcerningLoading] = useState(false);
  const [concerningError, setConcerningError] = useState("");

  const visibleSalespeople = useMemo(
    () =>
      salespeople.filter((sp) => {
        const name = ((sp && (sp.name || sp)) || "").trim().toLowerCase();
        return name && !EXCLUDED_SALESPEOPLE.has(name);
      }),
    [salespeople]
  );

  useEffect(() => {
    axios.defaults.withCredentials = true;

    axios.interceptors.request.use(
      (config) => {
        if (['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase())) {
          const csrfToken = localStorage.getItem('csrf_token');
          if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken;
          }
        }

        const isLogin = config.url?.includes('/auth/login');
        if (isLogin) {
          delete config.headers['Authorization'];
        }

        return config;
      },
      (error) => Promise.reject(error)
    );
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API_BASE}/auth/me`, {
          withCredentials: true,
          timeout: 3000,
        });
        setIsAuthenticated(true);
        setUserRole(res.data.role);
        localStorage.setItem('user_role', res.data.role);
        localStorage.setItem('linked_sales_person', res.data.linked_sales_person || '');
      } catch (err) {
        localStorage.removeItem('user_role');
        localStorage.removeItem('linked_sales_person');
        setIsAuthenticated(false);
        setUserRole(null);
      }
      setAuthLoading(false);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!canViewAllSalespersons()) {
      const linkedSalesperson = localStorage.getItem("linked_sales_person");
      if (linkedSalesperson) {
        setSelectedSalesperson(linkedSalesperson);
      }
    }
  }, [isAuthenticated, userRole]);

  const handleLoginSuccess = (userData) => {
    setIsAuthenticated(true);
    setUserRole(userData.role);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_BASE}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (err) {
      console.error('Logout error:', err);
    }

    localStorage.removeItem('user_role');
    localStorage.removeItem('linked_sales_person');
    localStorage.removeItem('csrf_token');

    setIsAuthenticated(false);
    setUserRole(null);
    setCurrentView('dashboard');
  };

  const loadSalespeople = async () => {
    try {
      setError("");
      const res = await axios.get(`${API_BASE}/api/salespeople`);
      const list = res.data.salespeople || [];
      setSalespeople(list);
    } catch (err) {
      console.error("Error loading salespeople:", err);
      setError(`Failed to load salespeople: ${err.message}`);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadSalespeople();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const loadYears = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/metrics/available-years`);
        const years = res.data?.years;
        const list = Array.isArray(years) ? years : [];
        const uniqueYears = list.filter((y) => y != null && Number.isInteger(y)).sort((a, b) => b - a);
        if (uniqueYears.length > 0) {
          setAvailableYears(uniqueYears);
          setSelectedYear((prevYear) => {
            if (!uniqueYears.includes(prevYear)) {
              return uniqueYears[0];
            }
            return prevYear;
          });
        }
      } catch (err) {
        console.error("Error loading available years", err);
        setAvailableYears([currentYear]);
      }
    };
    loadYears();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('[data-dropdown]')) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && fullScreen) {
        setFullScreen(null);
      }
      if (event.ctrlKey && event.key === 'r') {
        event.preventDefault();
        if (selectedSalesperson && selectedYear) {
          loadSalespeople();
          setDashboardBundle(null);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [fullScreen, selectedSalesperson, selectedYear]);

  useEffect(() => {
    if (currentView !== 'dashboard') return;

    const fetchBundle = async () => {
      if (!selectedSalesperson || !selectedYear) {
        setDashboardBundle(null);
        return;
      }

      try {
        setLoading(true);
        setError("");
        const res = await axios.get(`${API_BASE}/api/metrics/dashboard-bundle`, {
          params: {
            salesperson: selectedSalesperson,
            year: selectedYear,
            region: selectedRegion || "all",
            top_companies_limit: 15,
          },
          withCredentials: true,
        });
        setDashboardBundle(res.data);

        try {
          const clRes = await axios.get(`${API_BASE}/api/closed-lost/reasons`, {
            params: {
              salesperson: selectedSalesperson,
              year: selectedYear,
              business_unit: selectedRegion || "all",
            },
          });
          setClosedLostData(clRes.data || []);
        } catch (clErr) {
          console.error("Error loading closed lost data for bundle:", clErr);
        }
      } catch (err) {
        console.error("Error loading dashboard bundle:", err);
        setError(`Failed to load dashboard data: ${err.message}`);
        setDashboardBundle(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBundle();
  }, [currentView, selectedSalesperson, selectedYear, selectedRegion]);

  useEffect(() => {
    if (currentView !== 'dashboard' || !selectedSalesperson || !selectedYear) {
      setGoodBadMargin(null);
      setGoodBadMarginError("");
      return;
    }
    const fetchGoodBadMargin = async () => {
      try {
        setGoodBadMarginLoading(true);
        setGoodBadMarginError("");
        const res = await axios.get(`${API_BASE}/api/metrics/good-bad-margin-pairs`, {
          params: {
            salesperson: selectedSalesperson,
            year: selectedYear,
            region: selectedRegion || "all",
            limit: 10,
          },
        });
        const data = res.data || {};
        const high = Array.isArray(data.high_priority_enquiries) ? data.high_priority_enquiries : (data.data || []).filter((r) => r.pair_type === "Good");
        const concerning = Array.isArray(data.concerning_enquiries) ? data.concerning_enquiries : (data.data || []).filter((r) => r.pair_type === "Bad");
        setGoodBadMargin({ high_priority_enquiries: high, concerning_enquiries: concerning });
      } catch (err) {
        console.error("Good-bad margin pairs error", err);
        setGoodBadMarginError(err.message || "Failed to load margin pairs");
        setGoodBadMargin({ high_priority_enquiries: [], concerning_enquiries: [] });
      } finally {
        setGoodBadMarginLoading(false);
      }
    };
    fetchGoodBadMargin();
  }, [currentView, selectedSalesperson, selectedYear, selectedRegion]);

  useEffect(() => {
    if (currentView !== 'dashboard' || !selectedSalesperson || !selectedYear) {
      setConcerningEnquiries([]);
      setConcerningError("");
      return;
    }
    const fetchConcerningEnquiries = async () => {
      try {
        setConcerningLoading(true);
        setConcerningError("");
        const res = await axios.get(`${API_BASE}/api/metrics/concerning-enquiries`, {
          params: {
            salesperson: selectedSalesperson,
            year: selectedYear,
            region: selectedRegion || "all",
            limit: 10,
          },
        });
        const rows = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setConcerningEnquiries(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.error("Concerning enquiries error", err);
        setConcerningError(err.message || "Failed to load concerning enquiries");
        setConcerningEnquiries([]);
      } finally {
        setConcerningLoading(false);
      }
    };
    fetchConcerningEnquiries();
  }, [currentView, selectedSalesperson, selectedYear, selectedRegion]);

  const displayCurrency = getDisplayCurrency(selectedRegion);

  const filterContext = createFilterContext({
    salesperson: selectedSalesperson || null,
    year: selectedYear || null,
    region: selectedRegion || "all",
    displayCurrency,
  });

  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)'
      }}>
        <div style={{ color: '#1a2332', fontSize: '18px' }}>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div style={{
      padding: "24px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      paddingBottom: "100px",
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)",
      position: "relative",
    }}>
      <CursorFollower />
      <div
        style={{
          background: "linear-gradient(135deg, #1a2332 0%, #2a3f5f 50%, #1e293b 100%)",
          padding: "28px 40px",
          borderRadius: "16px",
          marginBottom: "32px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.1)",
          border: "2px solid #ff6b35",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "300px",
            height: "100%",
            background:
              "linear-gradient(135deg, rgba(42, 63, 95, 0.4) 0%, transparent 100%)",
            pointerEvents: "none",
          }}
        />
        <h1
          style={{
            margin: "0 0 8px 0",
            color: "#ffffff",
            fontSize: "32px",
            fontWeight: "700",
            letterSpacing: "-0.5px",
            textShadow: "0 2px 4px rgba(0,0,0,0.2)",
            position: "relative",
            zIndex: 1,
            fontFamily:
              "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
          }}
        >
          <span
            style={{
              color: "#ff8a3d",
              fontWeight: "700",
            }}
          >
            Retail Sales AI
          </span>{" "}
          <span
            style={{
              fontWeight: "700",
            }}
          >
            Dashboard
          </span>
        </h1>
        <p
          style={{
            margin: 0,
            color: "rgba(255,255,255,0.9)",
            fontSize: "15px",
            fontWeight: "400",
            letterSpacing: "0.3px",
            position: "relative",
            zIndex: 1,
            fontFamily:
              "'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
          }}
        >
          Powered by Blue Rhine Industries Analytics
        </p>
      </div>

      <div style={{
        position: "absolute",
        top: "28px",
        right: "40px",
        display: "flex",
        alignItems: "center",
        gap: "16px",
        zIndex: 2
      }}>
        {userRole === 'Admin' && (
          <button
            onClick={() => setCurrentView(currentView === 'dashboard' ? 'users' : 'dashboard')}
            style={{
              padding: "8px 16px",
              backgroundColor: currentView === 'users' ? "#ff6b35" : "rgba(255,255,255,0.1)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "500"
            }}
          >
            {currentView === 'dashboard' ? 'Manage Users' : 'Back to Dashboard'}
          </button>
        )}

        <span style={{
          color: "rgba(255,255,255,0.8)",
          fontSize: "14px"
        }}>
          {userRole && `Role: ${userRole.replace('_', ' ')}`}
        </span>
        <button
          onClick={handleLogout}
          style={{
            padding: "8px 16px",
            background: "rgba(255, 107, 53, 0.2)",
            color: "#ff6b35",
            border: "1px solid #ff6b35",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
            transition: "all 0.2s ease"
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#ff6b35";
            e.target.style.color = "white";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "rgba(255, 107, 53, 0.2)";
            e.target.style.color = "#ff6b35";
          }}
        >
          Logout
        </button>
      </div>

      {
        currentView === 'users' ? (
          <>
            <div
              style={{
                marginBottom: "32px",
                backgroundColor: "#e3f2fd",
                padding: "24px 28px",
                borderRadius: "16px",
                border: "2px solid #2196f3",
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <h3 style={{ margin: "0 0 8px 0", color: "#1565c0", fontSize: "18px" }}>
                ‍ Admin - User Management
              </h3>
              <p style={{ margin: 0, color: "#1976d2", fontSize: "14px" }}>
                Manage user accounts and use the AI Assistant to get insights about system usage.
              </p>
            </div>

            <div style={{ marginBottom: "32px" }}>
              <ErrorBoundary>
                <UserManagement />
              </ErrorBoundary>
            </div>

            <div style={{ marginBottom: "32px" }}>
              <ErrorBoundary>
                <AIAssistant
                  filterContext={{
                    salesperson: "Admin",
                    year: currentYear,
                    region: "all"
                  }}
                  userRole="Admin"
                />
              </ErrorBoundary>
            </div>
          </>
        ) : (
          <>

            {userRole === 'IT_Admin' ? (
              <>
                <div
                  style={{
                    marginBottom: "32px",
                    backgroundColor: "#e3f2fd",
                    padding: "24px 28px",
                    borderRadius: "16px",
                    border: "2px solid #2196f3",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                >
                  <h3 style={{ margin: "0 0 8px 0", color: "#1565c0", fontSize: "18px" }}>
                    ‍ IT Admin Dashboard
                  </h3>
                  <p style={{ margin: 0, color: "#1976d2", fontSize: "14px" }}>
                    Manage user accounts and use the AI Assistant to get system insights.
                  </p>
                </div>

                <div style={{ marginBottom: "32px" }}>
                  <ErrorBoundary>
                    <UserManagement />
                  </ErrorBoundary>
                </div>

                <div style={{ marginBottom: "32px" }}>
                  <ErrorBoundary>
                    <AIAssistant
                      filterContext={{
                        salesperson: "IT Admin",
                        year: currentYear,
                        region: "all"
                      }}
                      userRole="IT_Admin"
                    />
                  </ErrorBoundary>
                </div>
              </>
            ) : (
              <div
                style={{
                  marginBottom: "32px",
                  display: "flex",
                  gap: "20px",
                  alignItems: "center",
                  flexWrap: "wrap",
                  backgroundColor: "#ffffff",
                  padding: "24px 28px",
                  borderRadius: "16px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.05)",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.05)";
                }}
              >
              <label htmlFor="salesperson-select" style={{
                fontWeight: "600",
                color: "#1a2332",
                fontSize: "15px",
                letterSpacing: "0.2px",
              }}>Salesperson:</label>
              <div data-dropdown style={{ position: "relative", flex: 1, maxWidth: "420px" }}>
                <div
                  onClick={() => {
                    if (!canViewAllSalespersons()) return;
                    setDropdownOpen(!dropdownOpen);
                  }}
                  style={{
                    padding: "12px 20px",
                    fontSize: "15px",
                    backgroundColor: "#1e293b",
                    color: "#ffffff",
                    border: "2px solid #ff6b35",
                    borderRadius: "10px",
                    cursor: canViewAllSalespersons() ? "pointer" : "not-allowed",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    position: "relative",
                    transition: "all 0.2s ease",
                    fontWeight: "500",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#ff8555";
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#ff6b35";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  {selectedSalesperson && (() => {
                    const selectedPerson = visibleSalespeople.find(sp => (sp.name || sp) === selectedSalesperson);
                    const isActive = selectedPerson?.is_active !== undefined ? selectedPerson.is_active : true;
                    return (
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          backgroundColor: isActive ? "#4caf50" : "#f44336",
                          border: "2px solid #1e293b",
                          boxShadow: "0 0 0 1px " + (isActive ? "#4caf50" : "#f44336"),
                          flexShrink: 0
                        }}
                      />
                    );
                  })()}
                  <span style={{ flex: 1, textAlign: "left", fontWeight: "500" }}>
                    {selectedSalesperson || "Select a Salesperson"}
                  </span>
                  <span style={{
                    fontSize: "12px",
                    color: "#94a3b8",
                    transition: "transform 0.2s ease",
                    transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}>▼</span>
                </div>

                {dropdownOpen && canViewAllSalespersons() && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      marginTop: "6px",
                      backgroundColor: "#1e293b",
                      border: "2px solid #ff6b35",
                      borderRadius: "12px",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.1)",
                      zIndex: 1000,
                      maxHeight: "320px",
                      overflowY: "auto",
                      animation: "fadeIn 0.2s ease",
                      color: "#fffa"
                    }}
                  >
                    <div
                      onClick={() => {
                        setSelectedSalesperson("");
                        setDropdownOpen(false);
                      }}
                      style={{
                        padding: "10px 15px",
                        cursor: "pointer",
                        borderBottom: "1px solid #334155",
                        color: "#94a3b8",
                        fontSize: "14px"
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = "#334155"}
                      onMouseLeave={(e) => e.target.style.backgroundColor = "#1e293b"}
                    >
                      Select a Salesperson
                    </div>
                    {visibleSalespeople.map((sp) => {
                      const isActive = sp.is_active !== undefined ? sp.is_active : true;
                      const name = sp.name || sp;
                      return (
                        <div
                          key={name}
                          onClick={() => {
                            setSelectedSalesperson(name);
                            setDropdownOpen(false);
                          }}
                          style={{
                            padding: "10px 15px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            borderBottom: "1px solid #334155",
                            backgroundColor: selectedSalesperson === name ? "#334155" : "#1e293b",
                            color: "#fff"
                          }}
                          onMouseEnter={(e) => {
                            if (selectedSalesperson !== name) e.target.style.backgroundColor = "#334155";
                          }}
                          onMouseLeave={(e) => {
                            if (selectedSalesperson !== name) e.target.style.backgroundColor = "#1e293b";
                          }}
                        >
                          <div
                            style={{
                              width: "12px",
                              height: "12px",
                              borderRadius: "50%",
                              backgroundColor: isActive ? "#4caf50" : "#f44336",
                              border: "2px solid #ffffff",
                              boxShadow: "0 0 0 1px " + (isActive ? "#4caf50" : "#f44336"),
                              flexShrink: 0
                            }}
                          />
                          <span style={{ flex: 1 }}>{name}</span>
                          {!isActive && (
                            <span style={{ fontSize: "12px", color: "#999" }}>(Inactive)</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>

                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <label htmlFor="region-select" style={{ fontWeight: "600", color: "#1a2332", fontSize: "15px" }}>
                    <strong style={{ color: "#ff6b35" }}> Region:</strong>
                  </label>
                  <select
                    id="region-select"
                    value={selectedRegion}
                    onChange={(e) => {
                      setSelectedRegion(e.target.value);
                    }}
                    style={{
                      padding: "10px 16px",
                      fontSize: "14px",
                      backgroundColor: "#ffffff",
                      color: "#1a2332",
                      border: "2px solid #ff6b35",
                      borderRadius: "10px",
                      outline: "none",
                      cursor: "pointer",
                      fontWeight: "500",
                      minWidth: "180px",
                      transition: "all 0.2s ease",
                      boxShadow: "0 2px 4px rgba(255, 107, 53, 0.1)",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = "#ff8555";
                      e.target.style.boxShadow = "0 4px 8px rgba(255, 107, 53, 0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = "#ff6b35";
                      e.target.style.boxShadow = "0 2px 4px rgba(255, 107, 53, 0.1)";
                    }}
                  >
                    {availableRegions.map((region) => (
                      <option key={region} value={region}>
                        {region === 'all' ? 'All Regions' : region}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <label htmlFor="year-select" style={{ fontWeight: "600", color: "#1a2332", fontSize: "15px" }}>
                    <strong style={{ color: "#ff6b35" }}> Year:</strong>
                  </label>
                  <select
                    id="year-select"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    style={{
                      padding: "10px 16px",
                      fontSize: "15px",
                      backgroundColor: "#ffffff",
                      color: "#1a2332",
                      border: "2px solid #ff6b35",
                      borderRadius: "10px",
                      outline: "none",
                      cursor: "pointer",
                      fontWeight: "600",
                      transition: "all 0.2s ease",
                      boxShadow: "0 2px 4px rgba(255, 107, 53, 0.1)",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = "#ff8555";
                      e.target.style.boxShadow = "0 4px 8px rgba(255, 107, 53, 0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = "#ff6b35";
                      e.target.style.boxShadow = "0 2px 4px rgba(255, 107, 53, 0.1)";
                    }}
                  >
                    {availableYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={() => {
                    setLoading(true);
                    setError("");
                    loadSalespeople();
                    setSelectedSalesperson("");
                    setSelectedRegion("all");
                    setSelectedYear(currentYear);
                    setLoading(false);
                  }}
                  style={{
                    padding: "10px 28px",
                    cursor: "pointer",
                    background: "linear-gradient(135deg, #ff6b35 0%, #ff8555 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "10px",
                    fontWeight: "600",
                    fontSize: "14px",
                    marginLeft: "auto",
                    height: "42px",
                    boxShadow: "0 4px 12px rgba(255, 107, 53, 0.3), 0 2px 4px rgba(255, 107, 53, 0.2)",
                    transition: "all 0.2s ease",
                    letterSpacing: "0.3px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(255, 107, 53, 0.4), 0 3px 6px rgba(255, 107, 53, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(255, 107, 53, 0.3), 0 2px 4px rgba(255, 107, 53, 0.2)";
                  }}
                >
                   Refresh Data
                </button>
              </div>

              {loading && (
                <div style={{
                  color: "#ff6b35",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginTop: "12px",
                  fontSize: "14px",
                }}>
                  <span style={{ animation: "spin 1s linear infinite" }}>⏳</span>
                  Loading data...
                </div>
              )}
            </div>
            )}
            {/* Only show dashboard content for non-IT Admin users */}
            {userRole !== 'IT_Admin' && (
              <div style={{ background: 'linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%)', padding: '24px', borderRadius: 16, minHeight: 400 }}>
            {error && (
              <div style={{
                color: "#d32f2f",
                marginBottom: "24px",
                padding: "14px 18px",
                border: "2px solid #d32f2f",
                borderRadius: "12px",
                backgroundColor: "#ffebee",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 2px 8px rgba(211, 47, 47, 0.15)",
              }}>
                <span>{error}</span>
                <button
                  onClick={() => setError("")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#d32f2f",
                    cursor: "pointer",
                    fontSize: "18px",
                    fontWeight: "bold",
                    padding: "0 8px",
                    marginLeft: "10px"
                  }}
                  title="Dismiss"
                >
                  ×
                </button>
              </div>
            )}


            {filterContext.salesperson && (
              <div style={{ marginBottom: "50px" }}>
                <ErrorBoundary message="Failed to load Year Comparison. Please try refreshing.">
                  <YearComparison
                    filterContext={filterContext}
                    bundleData={dashboardBundle?.data?.year_comparison}
                    onMaximize={() => setFullScreen("yearComparison")}
                  />
                </ErrorBoundary>
              </div>
            )}

            {filterContext.salesperson && filterContext.year && (
              <div style={{ marginBottom: "50px" }}>
                <ErrorBoundary message="Failed to load Monthly Performance. Please try refreshing.">
                  <MonthlyPerformance
                    filterContext={filterContext}
                    bundleData={dashboardBundle?.data?.monthly_performance}
                    onMaximize={() => setFullScreen("monthly")}
                  />
                </ErrorBoundary>
              </div>
            )}

            {filterContext.salesperson && filterContext.year && (
              <div style={{ marginBottom: "50px" }}>
                <ErrorBoundary message="Failed to load Closed Lost Predictor. Please try refreshing.">
                  <ClosedLostPredictor
                    filterContext={filterContext}
                    onMaximize={() => setFullScreen("closedLostMonthly")}
                  />
                </ErrorBoundary>
              </div>
            )}

            {filterContext.salesperson && filterContext.year && (
              <div style={{ marginBottom: "50px" }}>
                <ErrorBoundary message="Failed to load Top Companies. Please try refreshing.">
                  <TopCompanies
                    filterContext={filterContext}
                    bundleData={dashboardBundle?.data?.top_companies}
                    onMaximize={() => setFullScreen("topCompanies")}
                  />
                </ErrorBoundary>
              </div>
            )}

            {filterContext.salesperson && filterContext.year && (
              <div
                style={{
                  display: "flex",
                  gap: "24px",
                  alignItems: "stretch",
                  marginBottom: "50px",
                  height: "850px",
                }}
              >
                <div
                  style={{
                    flex: "1 1 0",
                    width: "0",
                    display: "flex",
                  }}
                >
                  <ErrorBoundary message="Failed to load Probability Analysis. Please try refreshing.">
                    <ProbabilityAnalysis
                      filterContext={filterContext}
                      onMaximize={() => setFullScreen("probability")}
                    />
                  </ErrorBoundary>
                </div>

                <div
                  style={{
                    flex: "1 1 0",
                    width: "0",
                    display: "flex",
                  }}
                >
                  <ErrorBoundary message="Failed to load Pipeline Overview. Please try refreshing.">
                    <PipelineOverview
                      filterContext={filterContext}
                      bundleData={dashboardBundle?.data?.pipeline_overview}
                      onMaximize={() => setFullScreen("pipeline")}
                    />
                  </ErrorBoundary>
                </div>
              </div>
            )}

            {filterContext.salesperson && filterContext.year && (
              <div
                style={{
                  display: "flex",
                  gap: "24px",
                  alignItems: "stretch",
                  marginBottom: "50px",
                  height: "500px",
                }}
              >
                <div
                  style={{
                    flex: "1 1 0",
                    width: "0",
                    display: "flex",
                  }}
                >
                  <ErrorBoundary message="Failed to load Good Margin Pairs (Primary Focus). Please try refreshing.">
                    <GoodPairs
                      filterContext={filterContext}
                      onMaximize={() => setFullScreen("goodPairs")}
                      highPriorityEnquiries={goodBadMargin?.high_priority_enquiries ?? []}
                      loading={goodBadMarginLoading}
                      error={goodBadMarginError}
                    />
                  </ErrorBoundary>
                </div>
                <div
                  style={{
                    flex: "1 1 0",
                    width: "0",
                    display: "flex",
                  }}
                >
                  <ErrorBoundary message="Failed to load Concerning Enquiries. Please try refreshing.">
                    <BadPairs
                      filterContext={filterContext}
                      onMaximize={() => setFullScreen("badPairs")}
                      concerningEnquiries={concerningEnquiries}
                      loading={concerningLoading}
                      error={concerningError}
                    />
                  </ErrorBoundary>
                </div>
              </div>
            )}

            {filterContext.salesperson && filterContext.year && (
              <div style={{ marginBottom: "50px" }}>
                <ErrorBoundary message="Failed to load FOC Sample Tracker. Please try refreshing.">
                  <FOCSampleTracker
                    filterContext={filterContext}
                    onMaximize={() => setFullScreen("focSamples")}
                  />
                </ErrorBoundary>
              </div>
            )}

            {filterContext.salesperson && filterContext.year && (
              <div style={{ marginBottom: "50px" }}>
                <ErrorBoundary message="Failed to load Variation Tracker. Please try refreshing.">
                  <VariationTracker
                    filterContext={filterContext}
                    onMaximize={() => setFullScreen("variationTracker")}
                  />
                </ErrorBoundary>
              </div>
            )}

            {filterContext.salesperson && filterContext.year && (
              <div style={{ marginBottom: "50px" }}>
                <ErrorBoundary message="Failed to load Forecasted Booking. Please try refreshing.">
                  <ForecastedBooking
                    filterContext={filterContext}
                    onMaximize={() => setFullScreen("forecastedBooking")}
                  />
                </ErrorBoundary>
              </div>
            )}

            {filterContext.salesperson && filterContext.year && (
              <div style={{ marginBottom: "50px" }}>
                <ErrorBoundary message="Failed to load Closed Lost Reasons. Please try refreshing.">
                  <ClosedLostReasons
                    filterContext={filterContext}
                    onMaximize={() => setFullScreen("closedLostReasons")}
                  />
                </ErrorBoundary>
              </div>
            )}

            {filterContext.salesperson && filterContext.year && (
              <ErrorBoundary message="Failed to load Anomaly Assistant. Please try refreshing.">
                <AnomalyAssistant
                  filterContext={filterContext}
                  onMaximize={() => setFullScreen("anomalyAssistant")}
                />
            </ErrorBoundary>
          )}

          <AIAssistant
            filterContext={filterContext}
            bundleData={dashboardBundle}
              closedLostData={closedLostData}
              userRole={userRole}
            />

            {fullScreen && (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "linear-gradient(135deg, rgba(0,0,0,0.92) 0%, rgba(15,23,42,0.95) 100%)",
                  backdropFilter: "blur(8px)",
                  zIndex: 2000,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  animation: "fadeIn 0.3s ease",
                }}
                onClick={() => setFullScreen(null)}
              >
                <div
                  style={{
                    width: "78%",
                    height: "82%",
                    maxWidth: "1440px",
                    maxHeight: "920px",
                    background: "linear-gradient(135deg, #0b1220 0%, #1a2332 100%)",
                    borderRadius: 20,
                    border: "3px solid #ff6b35",
                    padding: "36px",
                    position: "relative",
                    overflow: "auto",
                    boxShadow: "0 24px 72px rgba(0,0,0,0.6), 0 8px 24px rgba(255, 107, 53, 0.2)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setFullScreen(null)}
                    style={{
                      position: "absolute",
                      top: "24px",
                      right: "48px",
                      background: "rgba(20, 26, 44, 0.8)",
                      border: "2px solid #ff6b35",
                      borderRadius: "50%",
                      width: "48px",
                      height: "48px",
                      color: "#ff6b35",
                      fontSize: "28px",
                      cursor: "pointer",
                      fontWeight: "bold",
                      zIndex: 3000,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.5), 0 0 0 4px rgba(255, 107, 53, 0.1)"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#ff6b35";
                      e.target.style.color = "#ffffff";
                      e.target.style.transform = "rotate(90deg) scale(1.1)";
                      e.target.style.boxShadow = "0 8px 24px rgba(255, 107, 53, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "rgba(20, 26, 44, 0.8)";
                      e.target.style.color = "#ff6b35";
                      e.target.style.transform = "rotate(0deg) scale(1)";
                      e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.5), 0 0 0 4px rgba(255, 107, 53, 0.1)";
                    }}
                    title="Close (ESC)"
                  >
                    ×
                  </button>
                  {fullScreen === "yearComparison" && (
                    <YearComparison filterContext={filterContext} fullScreen />
                  )}
                  {fullScreen === "monthly" && (
                    <MonthlyPerformance filterContext={filterContext} fullScreen />
                  )}
                  {fullScreen === "closedLostMonthly" && (
                    <ClosedLostPredictor filterContext={filterContext} fullScreen />
                  )}
                  {fullScreen === "goodPairs" && (
                    <GoodPairs
                      filterContext={filterContext}
                      fullScreen
                      highPriorityEnquiries={goodBadMargin?.high_priority_enquiries ?? []}
                      loading={goodBadMarginLoading}
                      error={goodBadMarginError}
                    />
                  )}
                  {fullScreen === "badPairs" && (
                  <BadPairs
                    filterContext={filterContext}
                    fullScreen
                    concerningEnquiries={concerningEnquiries}
                    loading={concerningLoading}
                    error={concerningError}
                  />
                )}
                  {fullScreen === "probability" && (
                    <ProbabilityAnalysis filterContext={filterContext} fullScreen />
                  )}
                  {fullScreen === "topCompanies" && (
                    <TopCompanies filterContext={filterContext} fullScreen />
                  )}
                  {fullScreen === "pipeline" && (
                    <PipelineOverview filterContext={filterContext} fullScreen />
                  )}
                  {fullScreen === "focSamples" && (
                    <FOCSampleTracker filterContext={filterContext} fullScreen />
                  )}
                  {fullScreen === "variationTracker" && (
                    <VariationTracker filterContext={filterContext} fullScreen />
                  )}
                  {fullScreen === "forecastedBooking" && (
                    <ForecastedBooking filterContext={filterContext} fullScreen />
                  )}
                  {fullScreen === "closedLostReasons" && (
                    <ClosedLostReasons filterContext={filterContext} fullScreen />
                  )}
                  {fullScreen === "anomalyAssistant" && (
                    <AnomalyAssistant filterContext={filterContext} fullScreen />
                  )}
                </div>
              </div>
            )}

              </div>
            )}
          </>
        )
      }

    </div >
  );
}

export default App;
